import { faChevronLeft } from '@fortawesome/pro-light-svg-icons';
import { faSync } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Grid } from '@mui/material';
import Loader from 'components/Loading/Loader';
import PageLayout from 'components/PageLayout';
import { useGetOrganizationBalanceQuery, useGetOrganizationQuery } from 'lib/graphql/graphql';
import useRefetchOnFocus from 'lib/graphql/useRefetchOnFocus';
import useFormattedDocumentTitle from 'lib/title/useFormattedDocumentTitle';
import StripeAccountBanner from 'modules/Payouts/components/StripeAccountBanner/StripeAccountBanner';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  ContainedLoadingButton,
  NeutralOutlinedButton,
  SmallTitle,
  Title,
} from 'styles/styles';
import { useLocation } from 'wouter';

import AmountCard from './components/AmountCard/AmountCard';
import PayoutsCard from './components/PayoutsCard/PayoutsCard';
import { AmountCardsRow, PayoutContent } from './styles';

function EventPayouts() {
  useFormattedDocumentTitle('payouts');
  const [, setLocation] = useLocation();

  const {
    data: organizationData,
    loading: loadingOrganization,
    refetch: refetchOrganization,
  } = useRefetchOnFocus(
    useGetOrganizationQuery({ fetchPolicy: 'network-only' }),
  );
  const organization = organizationData?.getOrganization;

  const {
    data: balanceData,
    loading: loadingBalance,
    refetch: refetchBalance,
  } = useRefetchOnFocus(
    useGetOrganizationBalanceQuery({
      fetchPolicy: 'network-only',
      variables: { id: organization?.id! },
      skip: !organization,
    }),
  );
  const balance = balanceData?.getOrganizationBalance;

  const [isRefreshing, setIsRefreshing] = useState(false);

  const handleRefresh = () => {
    setIsRefreshing(true);
    refetchOrganization();
    refetchBalance();
    setIsRefreshing(false);
  };

  return (
    <Loader isLoading={loadingOrganization || loadingBalance}>
      <PageLayout>
        <NeutralOutlinedButton
          onClick={() => setLocation('/')}
          startIcon={<FontAwesomeIcon size="1x" icon={faChevronLeft} />}
        >
          <FormattedMessage id="return_to_dashboard" />
        </NeutralOutlinedButton>
        <PayoutContent>
          <Grid container>
            <Grid item xs>
              <Title>
                <FormattedMessage id="payouts" />
              </Title>
            </Grid>
            <Grid item>
              <ContainedLoadingButton
                loading={isRefreshing}
                onClick={handleRefresh}
                startIcon={<FontAwesomeIcon icon={faSync} />}
              >
                <FormattedMessage id="refresh" />
              </ContainedLoadingButton>
            </Grid>
          </Grid>

          {organization && (
            <StripeAccountBanner organization={organization} />
          )}

          <Grid container spacing={2} justifyContent="center">
            <Grid item xs={12}>
              <SmallTitle>
                <FormattedMessage id="funds_in_stripe_account" />
              </SmallTitle>
            </Grid>
            <Grid item xs>
              <AmountCardsRow>
                <AmountCard
                  title={(<FormattedMessage id="payouts_total_volume" />)}
                  tooltip={(<FormattedMessage id="payouts_total_volume_tooltip" />)}
                  value={balance?.totalAmount || 0}
                  currency={organization?.currency || ''}
                />
                <AmountCard
                  title={(<FormattedMessage id="payouts_available_funds" />)}
                  tooltip={(<FormattedMessage id="payouts_available_funds_tooltip" />)}
                  value={balance?.availableAmount || 0}
                  currency={organization?.currency || ''}
                />
                <AmountCard
                  title={(<FormattedMessage id="payouts_pending_funds" />)}
                  tooltip={(<FormattedMessage id="payouts_pending_funds_tooltip" />)}
                  value={balance?.pendingAmount || 0}
                  currency={organization?.currency || ''}
                />
              </AmountCardsRow>
            </Grid>
          </Grid>

          {organization && (
            <PayoutsCard
              currency={organization?.currency || ''}
              organization={organization}
              paidAmount={balance?.paidAmount || 0}
            />
          )}
        </PayoutContent>
      </PageLayout>
    </Loader>
  );
}

export default EventPayouts;
